import { useState } from 'react'
import { MenuItem, TextField } from '@mui/material'
import {
  Alert,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import { IconButton } from '@mui/material'
import { FeeRate, Fund, MgmtFeeRun } from '../../api/types'
import { useOpsApi } from '../../api/context'
import { StatementButtons } from '../../components/StatementButtons'
import { apiError } from '../../api/errors'
import { QueueColumn, QueueTable } from '../../components/QueueTable'
import { InfoHint } from '../../components/InfoHint'
import { SectionCard } from '../../components/SectionCard'
import { formatCzk, formatDateTime, formatUnits } from '../jednotky/format'
import { FeeChecksPanel } from './FeeChecksPanel'
import { FeeRatesCard } from './FeeRatesCard'
import { downloadMgmtFeeDistributorReport, downloadMgmtFeeReport } from './report'

const ratePct = new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

interface MgmtFeeSectionProps {
  feeRates: FeeRate[]
  funds: Fund[]
  mgmtFeeRuns: MgmtFeeRun[]
  canManage: boolean
  onChanged: () => void
}

/**
 * Management fee: měsíční běh (UNIQUE per měsíc) = podklad pro provize distributorů.
 * MF je už v NAV od správce (13. 10. 2026), běh cenu jednotky nemění. Report per
 * distributor + rozpad per klient. Spouští backoffice/CEO (F6-D); ostatní read-only.
 */
export function MgmtFeeSection({ feeRates, funds, mgmtFeeRuns, canManage, onChanged }: MgmtFeeSectionProps) {
  const api = useOpsApi()
  // Výchozí = poslední uzavřený kvartál (provize se vyplácí kvartálně).
  const [period, setPeriod] = useState(lastClosedQuarter())
  const quarterOptions = recentQuarters()
  const [running, setRunning] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [viewRunId, setViewRunId] = useState<string | null>(null)

  const viewRun = mgmtFeeRuns.find((r) => r.id === viewRunId) ?? mgmtFeeRuns[0]

  const handleRun = async () => {
    setRunning(true)
    setError(null)
    try {
      const run = await api.runMgmtFee({ period })
      setViewRunId(run.id)
      onChanged()
    } catch (err) {
      setError(apiError(err, 'Přepočet management fee selhal'))
    } finally {
      setRunning(false)
    }
  }

  const historyColumns: QueueColumn<MgmtFeeRun>[] = [
    { key: 'period', label: 'Období', getSortValue: (r) => r.period, render: (r) => r.period },
    {
      key: 'clients',
      label: 'Klientů',
      align: 'right',
      render: (r) => r.lines.length,
    },
    {
      key: 'totalFee',
      label: 'Fee celkem',
      align: 'right',
      getSortValue: (r) => r.totalFee,
      render: (r) => formatCzk(r.totalFee),
    },
    {
      key: 'createdAt',
      label: 'Spustil',
      getSortValue: (r) => r.createdAt,
      render: (r) => (
        <Box>
          <Typography variant="body2">{r.createdBy}</Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            {formatDateTime(r.createdAt)}
          </Typography>
        </Box>
      ),
    },
  ]

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <FeeRatesCard feeRates={feeRates} funds={funds} canManage={canManage} onUpdated={onChanged} />

      <SectionCard
      title="Provize z management fee"
      info={
        <InfoHint
          title="Výpočet management fee"
          lines={[
            'Management fee strhává správce už v NAV. Běh tady nic nestrhává a cenu jednotky nemění, počítá jen provize distributorů.',
            'Základ klienta = jednotky přidělené před začátkem měsíce × cena ke konci měsíce × základna provizí p.a. / 12 (zatím 1,47 %). Vklad daného měsíce se počítá až od dalšího měsíce.',
            'Na konci měsíce nejdřív zadejte ocenění fondů, pak spusťte běh.',
            'Za každý měsíc lze běh spustit jen jednou (idempotence).',
            'Každý distributor vidí ve svém reportu jen své klienty. Účty Apexu (success fee, opravná položka) se nepočítají.',
            'Provize distributora = základ jeho klientů × jeho nasmlouvaný podíl (Distributoři → Podíl MF).',
          ]}
          sx={{ color: '#fff' }}
        />
      }
    >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              select
              size="small"
              label="Období"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              disabled={!canManage || running}
              sx={{ minWidth: 200 }}
            >
              {quarterOptions.map((q) => (
                <MenuItem key={q.value} value={q.value}>
                  {q.label}
                </MenuItem>
              ))}
            </TextField>
            <Button variant="contained" onClick={handleRun} disabled={!canManage || running}>
              Spustit přepočet
            </Button>
            {!canManage && (
              <Typography variant="caption" color="text.secondary">
                Přepočet spouští backoffice nebo CEO.
              </Typography>
            )}
          </Box>
          {error && <Alert severity="error">{error}</Alert>}
          <Alert severity="info" variant="outlined">
            Běh spočítá provize distributorů za zvolené období. Management fee je už v NAV od správce,
            běh cenu jednotky nemění. Výsledek se uloží do Historie běhů níže. U každého běhu
            stáhnete report celkem i pro jednotlivé distributory.
          </Alert>
        </Box>
      </SectionCard>

      {mgmtFeeRuns.length > 0 && (
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
            Historie běhů
          </Typography>
          <QueueTable
            columns={historyColumns}
            rows={mgmtFeeRuns}
            getRowKey={(r) => r.id}
            defaultSortKey="period"
            defaultSortDir="desc"
            emptyText="Žádné běhy"
            onRowClick={(r) => setViewRunId(r.id)}
          />
        </Box>
      )}

      {viewRun && (
        <SectionCard
          title={`Přehled ${viewRun.period}`}
          action={
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => downloadMgmtFeeReport(viewRun)}
              sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.6)' }}
            >
              Stáhnout report
            </Button>
          }
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ overflowX: 'auto' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Souhrn per distributor
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Distributor</TableCell>
                    <TableCell align="right">Klientů</TableCell>
                    <TableCell align="right">Objem</TableCell>
                    <TableCell align="right">Základ / měsíc</TableCell>
                    <TableCell align="right">
                      Provize distributora{' '}
                      <InfoHint
                        lines={[
                          'Základ × nasmlouvaný podíl distributora (Distributoři → Podíl MF).',
                        ]}
                      />
                    </TableCell>
                    <TableCell align="right">Vyúčtování</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {viewRun.byDistributor.map((d) => (
                    <TableRow key={d.distributorId || d.distributorName}>
                      <TableCell>{d.distributorName}</TableCell>
                      <TableCell align="right">{d.clientCount}</TableCell>
                      <TableCell align="right">{formatCzk(d.totalHoldingValue)}</TableCell>
                      <TableCell align="right">{formatCzk(d.totalFee)}</TableCell>
                      <TableCell align="right">{formatCzk(d.totalCommission ?? 0)}</TableCell>
                      <TableCell align="right">
                        {d.distributorId ? (
                          <StatementButtons
                            title={'Vyúčtování provize ' + d.distributorName}
                            load={(format) => api.mgmtFeeStatement(viewRun.id, d.distributorId, format)}
                            onError={setError}
                          />
                        ) : (
                          <IconButton
                            size="small"
                            title="Report jen pro tohoto distributora (vidí jen sebe)"
                            onClick={() =>
                              downloadMgmtFeeDistributorReport(viewRun, d.distributorId, d.distributorName)
                            }
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Celkem</TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {formatCzk(viewRun.totalFee)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {formatCzk(viewRun.totalCommission ?? 0)}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </Box>

            <Box sx={{ overflowX: 'auto' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Rozpad per klient
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Klient</TableCell>
                    <TableCell>Distributor</TableCell>
                    <TableCell align="right">Objem</TableCell>
                    <TableCell align="right">Sazba p.a.</TableCell>
                    <TableCell align="right">Fee / měsíc</TableCell>
                    <TableCell align="right">Podíl distr.</TableCell>
                    <TableCell align="right">Provize</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {viewRun.lines.map((l) => (
                    <TableRow key={l.clientId}>
                      <TableCell>{l.clientName}</TableCell>
                      <TableCell>{l.distributorName}</TableCell>
                      <TableCell align="right">{formatCzk(l.holdingValue)}</TableCell>
                      <TableCell align="right">{ratePct.format(l.ratePa * 100)} %</TableCell>
                      <TableCell align="right">{formatCzk(l.fee)}</TableCell>
                      <TableCell align="right">
                        {l.commissionRate != null && l.commissionRate > 0
                          ? (l.commissionRate * 100).toFixed(0) + ' %'
                          : '—'}
                      </TableCell>
                      <TableCell align="right">{formatCzk(l.commission ?? 0)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            {viewRun.fundReductions.length > 0 && (
              <Box sx={{ overflowX: 'auto' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Snížení hodnoty fondů (nepřímý dopad — cena jednotky klesla)
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Fond</TableCell>
                      <TableCell align="right">NAV před</TableCell>
                      <TableCell align="right">MF</TableCell>
                      <TableCell align="right">NAV po</TableCell>
                      <TableCell align="right">Cena jednotky po</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {viewRun.fundReductions.map((r) => (
                      <TableRow key={r.fundKey}>
                        <TableCell>{r.fundKey}</TableCell>
                        <TableCell align="right">{formatCzk(r.navBefore)}</TableCell>
                        <TableCell align="right">− {formatCzk(r.mfAmount)}</TableCell>
                        <TableCell align="right">{formatCzk(r.navAfter)}</TableCell>
                        <TableCell align="right">{formatUnits(r.unitPriceAfter)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}

            {viewRun.check && (
              <FeeChecksPanel check={viewRun.check} aiTitle="AI kontrola (per distributor)" />
            )}
          </Box>
        </SectionCard>
      )}
    </Box>
  )
}

/** Kvartál jako „2026-Q3“ — formát, který čeká backend. */
function quarterOf(date: Date): { year: number; q: number } {
  return { year: date.getFullYear(), q: Math.floor(date.getMonth() / 3) + 1 }
}

/** Poslední uzavřený kvartál (k 14. 10. 2026 = 2026-Q3). */
function lastClosedQuarter(): string {
  const now = quarterOf(new Date())
  const q = now.q === 1 ? 4 : now.q - 1
  const year = now.q === 1 ? now.year - 1 : now.year
  return `${year}-Q${q}`
}

/** Posledních 8 kvartálů (běžný včetně) pro výběr. */
function recentQuarters(): Array<{ value: string; label: string }> {
  const out: Array<{ value: string; label: string }> = []
  let { year, q } = quarterOf(new Date())
  for (let i = 0; i < 8; i++) {
    out.push({ value: `${year}-Q${q}`, label: `${q}. kvartál ${year}` })
    q -= 1
    if (q === 0) {
      q = 4
      year -= 1
    }
  }
  return out
}
